import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { addVoucherApi, deleteVoucherApi, getAllVoucherApi } from "@/api/voucher";
import { deleteC2CVoucherApi, getAllC2CVoucherApi, sendC2CGiftCardEmailApi } from "@/api/c2cvoucher";

// Thunks
export const fetchC2CVoucher = createAsyncThunk(
  "C2CVoucher/fetchVouchers",
  async (
    { restaurant_id, page = 1, search = "", status = "", per_page = 10 },
    { rejectWithValue }
  ) => {
    const params = new URLSearchParams();

    params.append("per_page", per_page);
    params.append("page", page);

    if (search) {
      params.append("search_term", search);
    }

    if (status) {
      params.append("status", status);
    }

    const res = await getAllC2CVoucherApi(restaurant_id, params.toString());

    if (res.status === "success") {
      return { ...res.data, search };
    }

    return rejectWithValue(res.message);
  }
);

export const deleteC2CVoucherById = createAsyncThunk(
  "C2CVoucher/delete",
  async (id, { rejectWithValue }) => {
    const res = await deleteC2CVoucherApi(id);
    if (res.status === "success") return id;
    return rejectWithValue(res.message);
  }
);

export const sendC2CVoucherEmail = createAsyncThunk(
  "C2CVoucher/sendEmail",
  async ({ id, data, onProgress, signal }, { rejectWithValue }) => {
    const config = {};
    if (signal) config.signal = signal;
    if (onProgress) {
      config.onDownloadProgress = (progressEvent) => {
        const loaded = progressEvent.loaded || 0;
        const total = progressEvent.total || null;
        let percent = null;
        if (total && total > 0) {
          percent = Math.round((loaded * 100) / total);
        }
        onProgress({ loaded, total, percent });
      };
    }
    const res = await sendC2CGiftCardEmailApi(id, data, config);
    if (res.status === "success") return res.data;
    return rejectWithValue(res.message);
  }
);

// Initial State
const initialState = {
  vouchers: [],
  singleVoucherCard: null,
  totalPages: 1,

  getLoading: false,
  createLoading: false,
  updateLoading: false,
  deleteLoading: false,

  success: null,
  error: null,
  requestStatus: false,
  search: "",
  status:"",
  perPage: 10,
  currentPage: 1
};

// Slice
const C2CVoucherSlice = createSlice({
  name: "voucher",
  initialState,
  reducers: {
    clearVoucherMessages: (state) => {
      state.success = null;
      state.error = null;
      state.requestStatus = false;
      state.totalPages = 1;
      state.search = "";
    },
    setVoucherSearch: (state, action) => {
      state.search = action.payload;
    },
    setStatus: (state, action) => {
      state.status = action.payload;
    },
    setPerPage: (state, action) => {
      state.perPage = action.payload;
    },
    setCurrentPage: (state, action) => {
      state.currentPage = action.payload;
    },
    updateC2CVoucherFromPusher: (state, action) => {
      const updatedCard = action.payload;
      if (!updatedCard || !updatedCard.id) return;
      if (Array.isArray(state.vouchers)) {
        const index = state.vouchers.findIndex((v) => v.id === updatedCard.id);
        if (index !== -1) {
          state.vouchers[index] = {
            ...state.vouchers[index],
            ...updatedCard,
          };
        }
      }
    },
  },
  extraReducers: (builder) => {
    builder

      // Fetch All
      .addCase(fetchC2CVoucher.pending, (state) => {
        state.getLoading = true;
        state.error = null;
        state.requestStatus = false;
      })
      .addCase(fetchC2CVoucher.fulfilled, (state, action) => {
        state.getLoading = false;
        state.vouchers = action.payload.c2c_gift_cards;
        state.requestStatus = true;
        state.totalPages = action.payload.pagination.last_page;
      })
      .addCase(fetchC2CVoucher.rejected, (state, action) => {
        state.getLoading = false;
        state.error = action.payload;
        state.requestStatus = false;
      })
      // Delete
      .addCase(deleteC2CVoucherById.pending, (state) => {
        state.deleteLoading = true;
        state.error = null;
        state.requestStatus = false;
      })
      .addCase(deleteC2CVoucherById.fulfilled, (state, action) => {
        state.deleteLoading = false;
        state.vouchers = state.vouchers.filter(
          (voucher) => voucher.id !== action.payload
        );
        state.success = "Voucher deleted successfully!";
        state.requestStatus = true;
      })
      .addCase(deleteC2CVoucherById.rejected, (state, action) => {
        state.deleteLoading = false;
        state.error = action.payload;
        state.requestStatus = false;
      })
      // Send Email
      .addCase(sendC2CVoucherEmail.pending, (state) => {
        state.updateLoading = true;
        state.error = null;
        state.requestStatus = false;
      })
      .addCase(sendC2CVoucherEmail.fulfilled, (state, action) => {
        state.updateLoading = false;
        state.success = "C2C Gift Card send request processed successfully!";
        state.requestStatus = true;
      })
      .addCase(sendC2CVoucherEmail.rejected, (state, action) => {
        state.updateLoading = false;
        state.error = action.payload;
        state.requestStatus = false;
      });
  },
});

export const { clearVoucherMessages, setVoucherSearch, setStatus, setPerPage, setCurrentPage, updateC2CVoucherFromPusher } = C2CVoucherSlice.actions;
export default C2CVoucherSlice.reducer;
